import React from 'react';
import { difference, uniq } from 'lodash';

import Optgroup from './Optgroup';
import Option from './Option';

class Multiselect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      multiple: Array.isArray(props.selected),
      searchTerm: '',           // ← new
    };

    this.isElementSelected = this.isElementSelected.bind(this);
    this.isGroupSelected = this.isGroupSelected.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.selectAll = this.selectAll.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.filterElements = this.filterElements.bind(this);
  }

  handleSearchChange(e) {
    this.setState({ searchTerm: e.target.value });
  }

  getElementProps(element) {
    if (element && typeof element === 'object' && 'value' in element) {
      return element;
    }
    return { label: String(element), value: element };
  }

  isElementSelected(value) {
    const { selected } = this.props;
    return this.state.multiple
      ? selected.includes(value)
      : selected === value;
  }

  isGroupSelected(elements) {
    const { selected } = this.props;
    if (!Array.isArray(selected) || selected.length === 0) return 'no';

    let found = 0;
    for (const el of elements) {
      const { value } = this.getElementProps(el);
      if (selected.includes(value)) found++;
    }

    if (found === 0) return 'no';
    if (found === elements.length) return 'yes';
    return 'partial';
  }
  
  // Returns true if EVERY item in the group is currently selected
  isGroupFullySelected(groupItems) {
    if (!this.state.multiple || !Array.isArray(groupItems)) return false;
  
    const { selected } = this.props;
    if (!Array.isArray(selected) || selected.length === 0) return false;
  
    return groupItems.every((item) => {
      const { value } = this.getElementProps(item);
      return selected.includes(value);
    });
  }
  
  // Returns array of selected values from this group
  getSelectedValuesInGroup(groupItems) {
    if (!this.state.multiple) return [];
  
    const { selected } = this.props;
    return groupItems
      .map((item) => this.getElementProps(item).value)
      .filter((value) => selected.includes(value));
  }

  selectAll(groupElements) {
    const { selected, handleChange } = this.props;
    const groupValues = groupElements.map((e) => this.getElementProps(e).value);
    const isAllSelected = groupValues.every((v) => selected.includes(v));

    if (isAllSelected) {
      handleChange(difference(selected, groupValues));
    } else {
      handleChange(uniq([...selected, ...groupValues]));
    }
  }

  handleChange(value) {
    let { selected, exclusiveValue = null, handleChange } = this.props;

    if (this.state.multiple) {
      let newSelected = [...selected];

      if (newSelected.includes(value)) {
        newSelected = newSelected.filter((v) => v !== value);
      } else {
        newSelected.push(value);
      }

      if (exclusiveValue !== null) {
        if (value === exclusiveValue) {
          newSelected = [exclusiveValue];
        } else {
          newSelected = newSelected.filter((v) => v !== exclusiveValue);
        }
      }

      handleChange(newSelected);
    } else {
      handleChange(value);
    }
  }

  filterElements(elements) {
    const term = this.state.searchTerm.trim().toLowerCase();
    if (!term) return elements;

    return elements.filter((el) => {
      const { label } = this.getElementProps(el);
      return String(label).toLowerCase().includes(term);
    });
  }

  renderElements(elements) {
    const filtered = this.filterElements(elements);
    if (filtered.length === 0) return null;

    return filtered.map((element) => {
      const { label, value } = this.getElementProps(element);
      return (
        <Option
          key={value}
          value={value}
          label={label}
          selected={this.isElementSelected(value)}
          handleChange={this.handleChange}
          type={this.state.multiple ? 'checkbox' : 'radio'}
        />
      );
    });
  }
  
  getLabel(itemOrGroup) {
    if (itemOrGroup.type === 'group') {
      return itemOrGroup.label;
    }
    return itemOrGroup.label;
  }
  
  caseInsensitiveCompare(a, b) {
    return this.getLabel(a).toLowerCase().localeCompare(
      this.getLabel(b).toLowerCase()
    );
  }
  
  getSelectedLabels() {
    const { selected, elements } = this.props;
    if (!selected || !this.state.multiple) {
      // single select fallback – keep simple for now
      if (!selected) return [];
      const found = this.findOptionByValue(selected);
      return found ? [{ type: 'single', value: selected, label: found.label }] : [];
    }
  
    const tags = [];
    const processedGroups = new Set(); // to avoid double-counting
  
    // 1. Check for fully selected groups first
    if (typeof elements === 'object' && elements !== null) {
      for (const groupName in elements) {
        const groupItems = elements[groupName];
        if (!Array.isArray(groupItems)) continue;
  
        const normalized = String(groupName).trim().toLowerCase();
  
        if (this.isGroupFullySelected(groupItems)) {
          tags.push({
            type: 'group',
            groupName,
            label: `${groupName} (all)`,
            groupItems, // so we can deselect all later
          });
          processedGroups.add(groupName);
  
          // Mark all these values as "handled" so they don't appear as individuals
          groupItems.forEach((item) => {
            const val = this.getElementProps(item).value;
            // we don't need to do anything else – just skip them later
          });
        }
      }
    }
  
    // 2. Now add individual selected items (that are not part of a fully-selected group)
    const selectedSet = new Set(selected);
  
    const addIndividual = (value, label) => {
      // Skip if this value belongs to a fully selected group
      let belongsToFullGroup = false;
  
      if (typeof elements === 'object' && elements !== null) {
        for (const gName in elements) {
          if (processedGroups.has(gName)) {
            const items = elements[gName];
            if (items.some((it) => this.getElementProps(it).value === value)) {
              belongsToFullGroup = true;
              break;
            }
          }
        }
      }
  
      if (!belongsToFullGroup) {
        tags.push({ type: 'option', value, label });
      }
    };
  
    selected.forEach((value) => {
      const found = this.findOptionByValue(value);
      const label = found ? found.label : String(value);
      addIndividual(value, label);
    });
  
    return tags;
  }
  
  findOptionByValue(targetValue) {
    const { elements } = this.props;
  
    const searchIn = (items) => {
      for (const el of items) {
        const { value, label } = this.getElementProps(el);
        if (value === targetValue) return { value, label };
      }
      return null;
    };
  
    if (Array.isArray(elements)) {
      return searchIn(elements);
    }
  
    for (const groupName in elements) {
      const groupItems = elements[groupName];
      if (Array.isArray(groupItems)) {
        const found = searchIn(groupItems);
        if (found) return found;
      }
    }
    return null;
  }
  
  removeSelected = (item) => {
    const { selected, handleChange } = this.props;
  
    if (!this.state.multiple) {
      handleChange(null);
      return;
    }
  
    let newSelected = [...selected];
  
    if (item.type === 'group') {
      // Deselect EVERY item in the group
      const groupValues = item.groupItems.map((el) => this.getElementProps(el).value);
      newSelected = newSelected.filter((v) => !groupValues.includes(v));
    } else if (item.type === 'option' || item.type === 'single') {
      // Deselect single item
      newSelected = newSelected.filter((v) => v !== item.value);
    }
  
    handleChange(newSelected);
  };

  renderOptionsOrGroups() {
    const { elements } = this.props;
    const term = this.state.searchTerm.trim().toLowerCase();
  
    // 1. Collect all top-level renderable items
    const topLevelItems = [];
  
    if (Array.isArray(elements)) {
      const filtered = this.filterElements(elements);
      filtered.forEach(el => {
        const props = this.getElementProps(el);
        topLevelItems.push({
          type: 'option',
          ...props
        });
      });
    } 
    else if (typeof elements === 'object' && elements !== null) {
      for (const groupName in elements) {
        let groupItems = elements[groupName];
        if (!Array.isArray(groupItems)) continue;
  
        const normalized = String(groupName).trim().toLowerCase();
        const filteredItems = this.filterElements(groupItems);
  
        if (filteredItems.length === 0) continue;
  
        if (normalized === 'unknown') {
          // Flatten unknown → treat as individual options
          filteredItems.forEach(el => {
            const props = this.getElementProps(el);
            topLevelItems.push({
              type: 'option',
              ...props
            });
          });
        } else {
          // Normal group
          topLevelItems.push({
            type: 'group',
            label: groupName,               // original case preserved
            originalItems: groupItems,      // for selectAll & group selection state
            filteredItems
          });
        }
      }
    }
  
    if (topLevelItems.length === 0 && term) {
      return <div className="no-results">No matching options</div>;
    }
  
    // 2. Sort top-level items (groups & flat options) case-insensitively
    topLevelItems.sort((a, b) => this.caseInsensitiveCompare(a, b));
  
    // 3. Render in sorted order
    return topLevelItems.map((entry, idx) => {
      if (entry.type === 'option') {
        return (
          <Option
            key={`flat-${entry.value || entry.label}-${idx}`}
            value={entry.value}
            label={entry.label}
            selected={this.isElementSelected(entry.value)}
            handleChange={this.handleChange}
            type={this.state.multiple ? 'checkbox' : 'radio'}
          />
        );
      }
  
      if (entry.type === 'group') {
        // Sort children inside the group too (case-insensitive)
        const sortedChildren = [...entry.filteredItems].sort((a, b) => {
          const la = this.getElementProps(a).label.toLowerCase();
          const lb = this.getElementProps(b).label.toLowerCase();
          return la.localeCompare(lb);
        });
  
        const childOptions = sortedChildren.map((el, childIdx) => {
          const { label, value } = this.getElementProps(el);
          return (
            <Option
              key={`child-${value || label}-${childIdx}`}
              value={value}
              label={label}
              selected={this.isElementSelected(value)}
              handleChange={this.handleChange}
              type={this.state.multiple ? 'checkbox' : 'radio'}
            />
          );
        });
  
        return (
          <Optgroup
            key={`group-${entry.label}-${idx}`}
            label={entry.label}
            selected={this.isGroupSelected(entry.originalItems)}
            selectAll={() => this.selectAll(entry.originalItems)}
          >
            {childOptions}
          </Optgroup>
        );
      }
  
      return null;
    });
  }

  render() {
    const { placeholder = "Search...", showSelectedTags = false } = this.props;
    const selectedTags = showSelectedTags ? this.getSelectedLabels() : [];
    return (
      <div className="form-control">
        {/* Search input */}
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input form-control"
            placeholder={placeholder}
            value={this.state.searchTerm}
            onChange={this.handleSearchChange}
          />
        </div>
        
        {/* Selected tags / chips */}
        {selectedTags.length > 0 && (
          <div className="selected-tags">
            {selectedTags.map((tag, index) => (
              <span key={`${tag.type}-${tag.value || tag.groupName}-${index}`} className="tag">
                {tag.label}
                <button
                  type="button"
                  className="tag-remove"
                  onClick={() => this.removeSelected(tag)}
                  aria-label={`Remove ${tag.label}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        
        {/* Options / groups */}
        <div className="options-container">
          {this.renderOptionsOrGroups()}
        </div>
      </div>
    );
  }
}

export default Multiselect;